<template>
  <div class="my-card">
    <b-button
      variant="primary"
      @click="showAddModal = true"
    >
      Tambah Jurnal
    </b-button>
    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Search</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
          />
        </div>
      </b-form-group>
    </div>

    <!-- table -->
    <vue-good-table
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
        selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: Action -->
        <span v-if="props.column.field === 'action'">
          <span v-if="props.row.no_reff === null || props.row.no_reff === ''">
            <router-link
              :to="{ name: 'acc-journal-edit', params: { id: props.row.uuid } }"
              class="btn btn-outline-warning btn-sm mr-1"
            >
              <feather-icon
                icon="EditIcon"
                size="16"
              />
            </router-link>

            <b-button
              size="sm"
              variant="outline-danger"
              @click="confirmDelete(props.row.uuid)"
            >
              <feather-icon
                icon="TrashIcon"
                size="16"
              />
            </b-button>
          </span>
          <span v-else>
            <b-button
              size="sm"
              variant="outline-info"
              @click="openEditModal(props.row)"
            >
              <feather-icon
                icon="EyeIcon"
                size="16"
              />
            </b-button>
          </span>
        </span>

        <!-- Column: Format total_debit and total_credit -->
        <span v-else-if="props.column.field === 'total_debit' || props.column.field === 'total_credit'">
          {{ Number(props.row[props.column.field]).toLocaleString('en-US') }}
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap ">
              Showing 1 to
            </span>
            <b-form-select
              v-model="pageLength"
              :options="['3','5','10']"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> of {{ props.total }} entries </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

    <b-modal
      id="edit-modal"
      v-model="isEditModalVisible"
      title="Detail Jurnal"
      size="lg"
      hide-footer
    >
      <p><strong>Kode Jurnal:</strong> {{ selectedTrxNo }}</p>

      <vue-good-table
        :columns="editTableColumns"
        :rows="editTableRows"
        :pagination-options="{ enabled: false }"
        style="margin-top: 1rem;"
        class="table table-bordered"
      >
        <!-- Format number jika perlu -->
        <template v-slot:table-row="props">
          <span v-if="props.column.field === 'debit' || props.column.field === 'credit'">
            {{ Number(props.row[props.column.field]).toLocaleString('en-US') }}
          </span>
          <span v-else>
            {{ props.row[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </b-modal>
    <!-- Modal Tambah Jurnal -->
    <b-modal
      v-model="showAddModal"
      title="Tambah Jurnal"
      hide-footer
    >
      <b-form
        @submit.prevent="saveNewJournal"
      >
        <b-form-group label="Tanggal">
          <b-form-datepicker
            v-model="newJournal.date"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            class="mb-2"
          />
          <small
            v-if="errors.date"
            class="text-danger"
          >
            {{ errors.date[0] }}
          </small>
        </b-form-group>

        <b-form-group label="Keterangan">
          <b-form-input
            v-model="newJournal.desc"
            type="text"
          /><small
            v-if="errors.desc"
            class="text-danger"
          >
            {{ errors.desc[0] }}
          </small>
        </b-form-group>

        <div class="text-right">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showAddModal = false"
          >
            Batal
          </b-button>
          <b-button
            type="submit"
            variant="primary"
          >
            Simpan
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="showDeleteModal"
      title="Konfirmasi Hapus"
      hide-footer
    >
      <p>Yakin mau hapus data ini?</p>
      <div class="text-right">
        <b-button
          variant="secondary"
          class="mr-2"
          @click="showDeleteModal = false"
        >
          Batal
        </b-button>
        <b-button
          variant="danger"
          @click="deleteItem"
        >
          Hapus
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BPagination, BFormGroup, BFormInput, BFormSelect,
  BButton, BModal, BFormDatepicker, BForm,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'

export default {
  components: {
    VueGoodTable,
    BForm,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    BButton,
    BModal,
  },
  data() {
    return {
      pageLength: 10,
      dir: false,
      columns: [
        { field: 'id', label: 'No' },
        { field: 'date', label: 'Tanggal' },
        { field: 'trx_no', label: 'Kode Jurnal' },
        { field: 'trx_no', label: 'Kode Jurnal' },
        { field: 'total_debit', label: 'Debit' },
        { field: 'total_credit', label: 'Kredit' },
        { field: 'desc', label: 'Desc' },
        { field: 'action', label: 'Action' },
      ],
      rows: [],
      searchTerm: '',

      selectedIDJournal: '',
      selectedTrxNo: '',
      isEditModalVisible: false,
      editTableRows: [],
      editTableColumns: [
        { label: 'Akun', field: 'account_name' },
        { label: 'Debit', field: 'debit' },
        { label: 'Kredit', field: 'credit' },
      ],

      showAddModal: false,
      newJournal: {
        date: '',
        desc: '',
      },
      errors: {}, // buat nampung error
      uuidToDelete: null,
      showDeleteModal: false,
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  created() {
    this.loadJournal()
  },
  methods: {
    loadJournal() {
      this.$http.get('/journal')
        .then(res => {
          let xid = 0
          this.rows = res.data.data.map(journal => {
            xid += 1
            // const balance = cash.balance.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            return {
              id: `#${xid}`,
              trx_no: journal.trx_no,
              date: journal.date,
              desc: journal.desc,
              total_debit: journal.total_debit,
              total_credit: journal.total_credit,
              uuid: journal.id,
              no_reff: journal.no_reff,
            }
          })
        })
    },
    async openEditModal(row) {
      try {
        const IdJournal = row.uuid
        const TrxNo = row.trx_no
        this.selectedTrxNo = TrxNo
        this.selectedIDJournal = IdJournal

        const response = await this.$http.get(`/journal/${IdJournal}`)

        // Asumsikan response.data.detail adalah array isi jurnal (akun, deskripsi, debit, kredit)
        this.editTableRows = response.data.details || []

        this.isEditModalVisible = true
      } catch (error) {
        console.error('Gagal mengambil detail jurnal:', error)
        this.$bvToast.toast('Gagal mengambil detail jurnal.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    confirmDelete(uuid) {
      this.uuidToDelete = uuid
      this.showDeleteModal = true
    },
    deleteItem() {
      console.log('UUID yang dikirim:', this.uuidToDelete)
      if (!this.uuidToDelete) {
        console.warn('UUID kosong, tidak hapus')
        return
      }
      if (!this.uuidToDelete) return

      this.$http.delete(`/journal/delete/${this.uuidToDelete}`)
        .then(() => {
          this.rows = this.rows.filter(r => r.uuid !== this.uuidToDelete)
          this.showDeleteModal = false
          this.uuidToDelete = null
          this.$bvToast.toast('Jurnal berhasil dihapus!', {
            title: 'Sukses',
            variant: 'success',
            solid: true,
          })
          this.loadJournal() // refresh tabel dari API
        })
        .catch(err => {
          console.error('Gagal hapus data', err)
        })
    },
    saveNewJournal() {
      this.$http.post('/journal/add', this.newJournal)
        .then(res => {
          this.showAddModal = false
          this.newJournal = { date: '', desc: '' } // reset form
          const newId = res.data.data.id // atau .uuid tergantung di DB
          this.$router.push({ name: 'acc-journal-edit', params: { id: newId } })
        })
        .catch(err => {
          if (err.response && err.response.status === 422) {
            this.errors = err.response.data.errors
          }
        })
    },

  },

}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
